import {
  DatePicker,
  FormField,
  NumberStepper,
  Select,
  Text,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { isQueued } from '../../offline/offline';
import { useColonies, useSaveMiteCount, type MiteCount, type MiteCountMethod } from '../api';
import { formatInfestation } from '../format';
import styles from './forms.module.css';

const METHODS: MiteCountMethod[] = ['natural_drop', 'powdered_sugar', 'alcohol_wash'];

export interface MiteCountDialogProps {
  open: boolean;
  onClose: () => void;
  mite?: MiteCount;
  colonyId?: string;
}

/** Mite count with live infestation rate (TRT-006). */
export function MiteCountDialog({ open, onClose, mite, colonyId }: MiteCountDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const colonies = useColonies({ status: 'active' }, open && !mite && !colonyId);
  const save = useSaveMiteCount();
  const [colony, setColony] = useState(colonyId ?? mite?.colony.id ?? '');
  const [countedOn, setCountedOn] = useState(mite?.counted_on ?? todayIso());
  const [method, setMethod] = useState<MiteCountMethod>(mite?.method ?? 'natural_drop');
  const [mites, setMites] = useState<number | null>(mite?.mites ?? null);
  const [days, setDays] = useState<number | null>(mite?.days ?? 3);
  const [bees, setBees] = useState<number | null>(mite?.bees ?? 300);
  const [notes, setNotes] = useState(mite?.notes ?? '');
  const colonyValue = colony || colonies.data?.[0]?.id || '';
  const errors = formErrors(save.error, {
    days: ['mite_count.days'],
    bees: ['mite_count.bees'],
  });
  const drop = method === 'natural_drop';
  const preview =
    mites !== null && (drop ? days : bees)
      ? formatInfestation(
          drop
            ? { unit: 'mites_per_day', value: mites / (days ?? 1) }
            : { unit: 'percent', value: (mites * 100) / (bees ?? 1) },
        )
      : undefined;
  const close = () => {
    save.reset();
    onClose();
  };
  const labels = { decrement: t('common.decrease'), increment: t('common.increase') };
  return (
    <FormDialog
      open={open}
      onClose={close}
      title={mite ? t('apiary.miteCount.editTitle') : t('apiary.miteCount.recordTitle')}
      description={mite?.colony.name}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          {
            colonyId: colonyValue,
            id: mite?.id,
            version: mite?.version,
            body: {
              counted_on: countedOn,
              method,
              mites: mites ?? 0,
              days: drop ? days : null,
              bees: drop ? null : bees,
              notes: notes || null,
            },
          },
          {
            onSuccess: (saved) => {
              if (isQueued(saved)) toast.show(t('offline.captured'), 'info');
              else toast.show(t('apiary.miteCount.saved'));
              close();
            },
          },
        )
      }
    >
      {!mite && !colonyId && (
        <FormField label={t('apiary.miteCount.colony')} required>
          {(p) => (
            <Select
              {...p}
              value={colonyValue}
              onChange={(e) => setColony(e.target.value)}
              options={(colonies.data ?? []).map((c) => ({
                value: c.id,
                label: `${c.name} · ${c.location.name}`,
              }))}
            />
          )}
        </FormField>
      )}
      <div className={styles.row}>
        <FormField label={t('apiary.miteCount.countedOn')} required>
          {(p) => <DatePicker {...p} value={countedOn} onChange={setCountedOn} />}
        </FormField>
        <FormField label={t('apiary.miteCount.method')}>
          {(p) => (
            <Select
              {...p}
              value={method}
              onChange={(e) => setMethod(e.target.value as MiteCountMethod)}
              options={METHODS.map((m) => ({ value: m, label: t(`apiary.miteMethod.${m}`) }))}
            />
          )}
        </FormField>
      </div>
      <div className={styles.row}>
        <FormField label={t('apiary.miteCount.mites')} required>
          {(p) => (
            <NumberStepper {...p} value={mites} onChange={setMites} min={0} labels={labels} />
          )}
        </FormField>
        {drop ? (
          <FormField label={t('apiary.miteCount.days')} required error={errors.field.days}>
            {(p) => (
              <NumberStepper
                {...p}
                value={days}
                onChange={setDays}
                min={1}
                max={60}
                labels={labels}
              />
            )}
          </FormField>
        ) : (
          <FormField label={t('apiary.miteCount.bees')} required error={errors.field.bees}>
            {(p) => (
              <NumberStepper
                {...p}
                value={bees}
                onChange={setBees}
                min={1}
                step={50}
                labels={labels}
              />
            )}
          </FormField>
        )}
      </div>
      {preview && (
        <Text weight="semibold" role="status">
          {t('apiary.miteCount.result', { value: preview })}
        </Text>
      )}
      <FormField label={t('apiary.miteCount.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
